import { useQuery, gql } from '@apollo/client';
import * as immutable from 'object-path-immutable';
import { formatTemplates } from 'components/templates/shared/helpers';
import { TEMPLATE_TYPES } from 'components/templates/shared/constants';

const DOCS_PER_PAGE = 15;

const mergeDocumentNodes = (oldData, { fetchMoreResult: newData }) => {
  if (!newData) {
    return oldData;
  }

  const oldNodes = oldData?.viewer?.company?.documentBlueprints.nodes ?? [];
  const newNodes = newData?.viewer?.company?.documentBlueprints.nodes ?? [];

  const combinedNodes = [...oldNodes, ...newNodes];

  return immutable.set(
    newData,
    'viewer.company.documentBlueprints.nodes',
    combinedNodes,
  );
};

export const GET_DOCUMENT_BLUEPRINTS = gql`
  query GetDocumentBlueprints($first: Int, $after: String) {
    viewer {
      id
      company {
        id
        documentBlueprints(first: $first, after: $after) {
          nodes {
            id
            name
            databaseId
            document {
              url
              contentType
              filename
            }
            updatedAt
          }

          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`;

const useGetCompanyDocuments = () => {
  const { data, loading, fetchMore, networkStatus } = useQuery(
    GET_DOCUMENT_BLUEPRINTS,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        first: DOCS_PER_PAGE,
      },
    },
  );

  const companyDocuments = data?.viewer?.company.documentBlueprints.nodes ?? [];
  const pageInfo = data?.viewer.company.documentBlueprints.pageInfo ?? {};

  const fetchMoreDocuments = () => {
    if (pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          first: DOCS_PER_PAGE,
          after: pageInfo.endCursor,
        },
        updateQuery: mergeDocumentNodes,
      });
    }
  };

  const formattedCompanyDocuments = formatTemplates({
    templates: companyDocuments,
    type: TEMPLATE_TYPES.COMPANY_DOCUMENT,
  });

  return {
    companyDocuments: formattedCompanyDocuments,
    loading,
    fetchMore: fetchMoreDocuments,
    hasNextPage: pageInfo?.hasNextPage,
    networkStatus,
  };
};

export default useGetCompanyDocuments;
